import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { exportAuditLogs, fetchAuditLogs } from "../../api/auditApi";
import ModuleSearchControls, {
  ModuleFilterMenu,
  ModulePerPageMenu,
  ModuleRadioOption,
  ModuleSortMenu,
} from "../../components/ModuleSearchControls";
import PageAlert from "../../components/PageAlert/PageAlert";
import DataTable, { DataTableFooter } from "../../components/DataTable";
import { resolveApiError } from "../../utils/apiError";
import "./audit-trail.scss";

const SORT_OPTIONS = [
  { value: "created_at", label: "Timestamp" },
  { value: "user_name", label: "User name" },
  { value: "user_email", label: "User email" },
  { value: "module", label: "Module" },
  { value: "action", label: "Action" },
];

const showData = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "string") return value;
  try {
    return Object.entries(value)
      .map(([k, v]) => `${k}: ${Array.isArray(v) || typeof v === "object" ? JSON.stringify(v) : v}`)
      .join(" · ");
  } catch {
    return String(value);
  }
};

const mergeOptions = (current, incoming = []) => {
  const next = [...current];
  incoming.forEach((item) => {
    if (item && !next.includes(item)) next.push(item);
  });
  return next.sort((left, right) => String(left).localeCompare(String(right)));
};

const AuditTrailPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [module, setModule] = useState("");
  const [action, setAction] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [perPageOpen, setPerPageOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const filterParams = useCallback(
    () => ({
      search: debouncedQuery || undefined,
      module: module || undefined,
      action: action || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
    }),
    [debouncedQuery, module, action, dateFrom, dateTo, sortBy, sortOrder]
  );

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchAuditLogs({
        ...filterParams(),
        page,
        per_page: perPage,
      });
      setEntries(result.logs || []);
      setMeta(result.meta);
    } catch (e) {
      resolveApiError(e, setError, navigate);
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, perPage]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    let active = true;
    fetchAuditLogs({ page: 1, per_page: 100, sort_by: "created_at", sort_order: "desc" })
      .then((result) => {
        if (!active) return;
        const logs = result.logs || [];
        setModules((current) =>
          mergeOptions(
            current,
            result.modules?.length ? result.modules : logs.map((entry) => entry.module)
          )
        );
        setActions((current) =>
          mergeOptions(
            current,
            result.actions?.length ? result.actions : logs.map((entry) => entry.action)
          )
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const activeFilterCount = [module, action, dateFrom, dateTo].filter(Boolean).length;

  const reset = () => {
    setQuery("");
    setDebouncedQuery("");
    setModule("");
    setAction("");
    setDateFrom("");
    setDateTo("");
    setSortBy("created_at");
    setSortOrder("desc");
    setPerPage(20);
    setPage(1);
  };

  const exportLog = async () => {
    setExporting(true);
    setError("");
    try {
      await exportAuditLogs(filterParams());
    } catch (e) {
      resolveApiError(e, setError, navigate);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="audit-page">
      {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}
      <section className="audit-page__heading">
        <div>
          <span>
            <Icon icon="ph:list-checks" height={18} />
          </span>
          <div>
            <h2>Audit Trail</h2>
            <p>Review administrative, product and website configuration changes.</p>
          </div>
        </div>
        <button className="ui-button" onClick={exportLog} disabled={exporting}>
          <Icon icon="ph:download-simple" height={14} /> {exporting ? "Exporting…" : "Export log"}
        </button>
      </section>
      <section className="audit-page__card">
        <ModuleSearchControls
          value={query}
          onChange={setQuery}
          placeholder="Search by user, module or action"
        >
          <ModuleFilterMenu
            open={filterOpen}
            onToggle={() => {
              setFilterOpen((open) => !open);
              setSortOpen(false);
              setPerPageOpen(false);
            }}
            onClose={() => setFilterOpen(false)}
            count={activeFilterCount}
            ariaLabel="Audit filters"
          >
            <fieldset>
              <legend>Module</legend>
              <ModuleRadioOption
                name="audit-module"
                checked={!module}
                onChange={() => {
                  setModule("");
                  setPage(1);
                }}
              >
                All modules
              </ModuleRadioOption>
              {modules.map((item) => (
                <ModuleRadioOption
                  key={item}
                  name="audit-module"
                  checked={module === item}
                  onChange={() => {
                    setModule(item);
                    setPage(1);
                  }}
                >
                  {item}
                </ModuleRadioOption>
              ))}
            </fieldset>
            <fieldset>
              <legend>Action</legend>
              <ModuleRadioOption
                name="audit-action"
                checked={!action}
                onChange={() => {
                  setAction("");
                  setPage(1);
                }}
              >
                All actions
              </ModuleRadioOption>
              {actions.map((item) => (
                <ModuleRadioOption
                  key={item}
                  name="audit-action"
                  checked={action === item}
                  onChange={() => {
                    setAction(item);
                    setPage(1);
                  }}
                >
                  {item}
                </ModuleRadioOption>
              ))}
            </fieldset>
            <fieldset>
              <legend>Date range</legend>
              <label className="module-filter-panel__date">
                <span>From</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                />
              </label>
              <label className="module-filter-panel__date">
                <span>To</span>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                />
              </label>
            </fieldset>
          </ModuleFilterMenu>
          <ModuleSortMenu
            open={sortOpen}
            onToggle={() => {
              setSortOpen((open) => !open);
              setFilterOpen(false);
              setPerPageOpen(false);
            }}
            onClose={() => setSortOpen(false)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            options={SORT_OPTIONS}
            onChangeSortBy={(value) => {
              setSortBy(value);
              setPage(1);
            }}
            onChangeSortOrder={(value) => {
              setSortOrder(value);
              setPage(1);
            }}
          />
          <ModulePerPageMenu
            open={perPageOpen}
            onToggle={() => {
              setPerPageOpen((open) => !open);
              setFilterOpen(false);
              setSortOpen(false);
            }}
            onClose={() => setPerPageOpen(false)}
            value={perPage}
            onChange={(value) => {
              setPerPage(value);
              setPage(1);
            }}
          />
          <button className="ui-button" type="button" onClick={reset}>
            <Icon icon="ph:arrow-clockwise" height={14} /> Reset
          </button>
        </ModuleSearchControls>
        <DataTable
          minWidth={1180}
          loading={loading}
          loadingMessage="Loading audit records…"
          emptyMessage="No audit records match the selected filters."
          rows={entries}
          columns={[
            {
              key: "timestamp",
              header: "Timestamp",
              render: (entry) => (
                <time>{entry.created_at ? new Date(entry.created_at).toLocaleString("en-MY") : "—"}</time>
              ),
            },
            {
              key: "user",
              header: "User",
              render: (entry) => (
                <div className="audit-page__user">
                  <span>
                    {(entry.user_name || "?")
                      .split(" ")
                      .map((word) => word[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                  <div>
                    <strong>{entry.user_name || "Unknown"}</strong>
                    <small>{entry.user_email || "—"}</small>
                  </div>
                </div>
              ),
            },
            {
              key: "module",
              header: "Module",
              render: (entry) => <span className="audit-page__module">{entry.module || "—"}</span>,
            },
            {
              key: "action",
              header: "Action",
              render: (entry) => <span className="audit-page__module">{entry.action || "—"}</span>,
            },
            { key: "details", header: "Action details", render: (entry) => entry.details || entry.description || "—" },
            {
              key: "old_data",
              header: "Old data",
              className: "audit-page__old",
              render: (entry) => showData(entry.old_data),
            },
            {
              key: "new_data",
              header: "New data",
              className: "audit-page__new",
              render: (entry) => showData(entry.new_data),
            },
          ]}
        />
        <DataTableFooter meta={meta} page={page} loading={loading} onPageChange={setPage} noun="entries" />
      </section>
    </div>
  );
};

export default AuditTrailPage;
